import { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Check, Mail, MessageCircle, Facebook, X as XIcon } from "lucide-react";

/**
 * Bouton de partage universel :
 * - Utilise la Web Share API native sur mobile
 * - Ouvre un menu avec WhatsApp / Facebook / X / Email / Copie du lien sur desktop
 */
export default function ShareEpisode({ episode, variant = "icon", onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = episode.link || window.location.href;
  const shareText = `« ${episode.title} » — Jesus Talk 🙏`;
  const fullText = `${shareText}\n\nÉcoute-le maintenant : ${shareUrl}`;

  const setMenuOpen = (v) => {
    setOpen(v);
    onOpenChange?.(v);
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      try {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
        return;
      } catch { /* user cancelled, fall through */ }
    }
    setMenuOpen(!open);
  };

  const copyLink = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié dans le presse-papiers");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const stop = (e) => e.stopPropagation();

  const links = {
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(fullText)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(fullText)}`,
  };

  const isPill = variant === "pill";

  return (
    <div className="relative" onClick={stop}>
      <button
        data-testid={`share-btn-${episode.id}`}
        onClick={handleClick}
        aria-label="Partager"
        className={
          isPill
            ? "inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E5E0D8] text-[#162238] text-sm hover:bg-[#F4EFE6] transition"
            : "h-9 w-9 rounded-full bg-white/90 hover:bg-white text-[#162238] flex items-center justify-center backdrop-blur transition jt-soft-shadow"
        }
      >
        <Share2 className="w-4 h-4" strokeWidth={1.75} />
        {isPill && <span>Partager</span>}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }}
          />
          <div
            data-testid={`share-menu-${episode.id}`}
            className="absolute right-0 mt-2 w-64 rounded-2xl bg-white border border-[#E5E0D8] jt-medium-shadow z-50 overflow-hidden jt-fadeUp"
            onClick={stop}
          >
            <div className="px-4 py-3 border-b border-[#F4EFE6]">
              <p className="text-xs uppercase tracking-widest text-[#D9A05B]">Partager cet épisode</p>
              <p className="text-sm text-[#162238] font-serif line-clamp-1 mt-0.5">{episode.title}</p>
            </div>
            <div className="p-2 flex flex-col">
              <ShareItem
                testid={`share-whatsapp-${episode.id}`}
                href={links.whatsapp}
                icon={<MessageCircle className="w-4 h-4 text-[#25D366]" />}
                label="WhatsApp"
              />
              <ShareItem
                testid={`share-facebook-${episode.id}`}
                href={links.facebook}
                icon={<Facebook className="w-4 h-4 text-[#1877F2]" />}
                label="Facebook"
              />
              <ShareItem
                testid={`share-x-${episode.id}`}
                href={links.x}
                icon={<XIcon className="w-4 h-4 text-[#162238]" />}
                label="X (Twitter)"
              />
              <ShareItem
                testid={`share-email-${episode.id}`}
                href={links.email}
                icon={<Mail className="w-4 h-4 text-[#D9A05B]" />}
                label="Email"
              />
              <button
                data-testid={`share-copy-${episode.id}`}
                onClick={copyLink}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F4EFE6] transition text-sm text-[#162238] text-left"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-[#6B7280]" />}
                {copied ? "Lien copié !" : "Copier le lien"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ShareItem({ href, icon, label, testid }) {
  return (
    <a
      data-testid={testid}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F4EFE6] transition text-sm text-[#162238]"
    >
      {icon}
      {label}
    </a>
  );
}
